import { Component, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';

import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';

import { environment } from '../../../../../../environments/environment';
import { SelectPesquisavelComponent } from '../../../../../shared/components/select-pesquisavel/select-pesquisavel.component';
import { SelectPesquisavelService } from '../../../../../shared/components/select-pesquisavel/select-pesquisavel.service';
import { TipoPessoaEnum } from '../../../../../shared/enums/pessoa/tipo-pessoa.enum';
import { AlertService, AlertType } from '../../../../../shared/services/alert.service';
import { CEPCorreios, UtilService } from '../../../../../shared/services/util.service';
import { MaskService, Masks } from '../../../../../shared/services/mask.service';
import { MunicipioItem } from '../../../../../shared/services/apis/usuario/municipio.service';
import { UnidadeFederativaService, UfOption } from '../../../../../shared/services/apis/usuario/unidade-federativa.service';

/**
 * Painel do endereco de entrega (§5.3), que o toggle da fileira 6 abre.
 *
 * Some INTEIRO quando o toggle esta desligado, e nao apenas fica desabilitado: com entrega no
 * endereco do cadastro, estes campos nao sao gravados nem exigidos — deixa-los na tela sugeriria
 * que valem alguma coisa.
 *
 * UF e Município seguem o padrão da casa (UF antes; Município do cadastro, filtrado pela UF,
 * desabilitado sem UF). O CONTRATO do documento guarda o endereço como texto (`entregaCidade` e
 * `entregaUf` como sigla) — é snapshot para a nota — então o município escolhido é mapeado para
 * `entregaCidade` explicitamente, e o id fica no controle de tela `entregaIdMunicipio` do shell.
 */
@Component({
    selector: 'app-vd-endereco-entrega',
    standalone: true,
    imports: [
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        MatIconModule,
        MatProgressSpinnerModule,
        NgxMaskDirective,
        SelectPesquisavelComponent,
    ],
    // A diretiva de mascara precisa do provider em quem a usa — sem ele, criar o componente
    // estoura NG0201 e o painel nao abre. E o mesmo padrao do cadastro de pessoa e de produto.
    providers: [provideNgxMask()],
    templateUrl: './vd-endereco-entrega.component.html',
    styleUrl: './vd-endereco-entrega.component.css',
})
export class VdEnderecoEntregaComponent implements OnInit {
    @Input({ required: true }) public form!: FormGroup;
    @Input() public isSomenteLeitura: boolean = false;

    public readonly tipoPessoaEnum = TipoPessoaEnum;

    public isBuscandoCep: boolean = false;

    /** Máscaras da casa, aplicadas pela diretiva do ngx-mask como no cadastro de Pessoa. */
    public readonly masks: Masks = MaskService.getMasks();

    /** Do cadastro, nao de uma lista escrita a mao: o servico e a fonte unica de UF do sistema. */
    public ufs: UfOption[] = [];

    /** O município já escolhido, entregue ao select como opção conhecida (a busca é paginada). */
    public municipiosPreCarregados: MunicipioItem[] = [];

    constructor(
        private alertService: AlertService,
        private utilService: UtilService,
        private unidadeFederativaService: UnidadeFederativaService,
        private selectPesquisavelService: SelectPesquisavelService,
    ) {}

    public ngOnInit(): void {
        // O documento gravado traz o CPF/CNPJ sem separadores; o campo mostra formatado, como ao digitar.
        const documento = this.form.get('entregaCpfCnpj');
        if (documento?.value) documento.setValue(MaskService.formatDocumento(documento.value), { emitEvent: false });

        this.unidadeFederativaService.getUnidadesFederativas().subscribe((lista) => {
            this.ufs = lista;
            // Em edição: primeiro a UF (já veio no form), depois habilita e restaura o Município.
            this.atualizarEstadoDoMunicipio(false);
            this.restaurarMunicipioGravado();
        });
    }

    /** CPF ou CNPJ conforme o tamanho (CNPJ alfanumérico), pelo formatador da casa. */
    public onDocumentoInput(evento: Event): void {
        MaskService.onDocumentoInput(evento, this.form.get('entregaCpfCnpj'));
    }

    /** O controle de tela do shell (`entregaIdMunicipio`): existe para o required e o `disabled` dele. */
    public get municipioControl(): FormControl {
        return this.form.get('entregaIdMunicipio') as FormControl;
    }

    private get idUnidadeFederativa(): number | null {
        const sigla = this.form.get('entregaUf')?.value;

        return this.ufs.find((u) => u.id === sigla)?.idUnidadeFederativa ?? null;
    }

    /** A busca do select é da API de Usuário, filtrada pela UF escolhida (como no cadastro de vendedor). */
    public get urlMunicipio(): string {
        return `${environment.urlApiUsuario}Municipio/select?idUnidadeFederativa=${this.idUnidadeFederativa ?? 0}`;
    }

    /** Trocar ou limpar a UF limpa o Município, o preload e o texto gravado (padrão da casa). */
    public aoTrocarUf(): void {
        this.atualizarEstadoDoMunicipio(true);
    }

    /** O nome do município escolhido é o que o contrato grava em `entregaCidade`. */
    public aoEscolherMunicipio(item: MunicipioItem | null): void {
        this.form.patchValue({ entregaCidade: item?.nome ?? null });
    }

    private atualizarEstadoDoMunicipio(limpar: boolean): void {
        if (limpar) {
            this.municipioControl?.setValue(null, { emitEvent: false });
            this.form.patchValue({ entregaCidade: null }, { emitEvent: false });
            this.municipiosPreCarregados = [];
        }

        if (this.isSomenteLeitura) return;

        if (this.idUnidadeFederativa) this.municipioControl?.enable({ emitEvent: false });
        else this.municipioControl?.disable({ emitEvent: false });
    }

    /**
     * Documento gravado: o contrato tem só o NOME da cidade e a UF. Procura o município da UF pelo
     * nome para o select mostrar a escolha; sem correspondência o texto gravado fica como está.
     */
    private restaurarMunicipioGravado(): void {
        const cidade = String(this.form.get('entregaCidade')?.value ?? '').trim();

        if (!cidade || !this.idUnidadeFederativa || this.municipioControl?.value) return;

        this.selectPesquisavelService.getSelectValues<MunicipioItem>(this.urlMunicipio, cidade).subscribe({
            next: (municipios) => {
                const municipio = municipios.find((m) => m.nome.localeCompare(cidade, 'pt-BR', { sensitivity: 'base' }) === 0);

                if (!municipio) return;

                this.municipiosPreCarregados = [municipio];
                this.municipioControl?.setValue(municipio.idMunicipio, { emitEvent: false });
            },
        });
    }

    /**
     * Busca o endereço pelo CEP, como o cadastro de Pessoa faz.
     *
     * NÃO sobrescreve número nem complemento: o CEP conhece a rua, não a casa — e quem já digitou
     * o número antes de completar o CEP perderia o que escreveu. O município vem pelo código IBGE
     * do CEP, para o select ficar preenchido e não só o texto.
     */
    public buscarCep(): void {
        const cep = String(this.form.get('entregaCep')?.value ?? '').replace(/\D/g, '');

        if (cep.length !== 8) return;

        this.isBuscandoCep = true;

        this.utilService.getAddressByCEP(cep).subscribe({
            next: (res: CEPCorreios) => {
                this.isBuscandoCep = false;

                if (!res?.cep) {
                    this.alertService.show('Aviso', 'CEP não encontrado.', AlertType.warning);
                    return;
                }

                this.form.patchValue({
                    entregaLogradouro: res.logradouro,
                    entregaBairro: res.bairro,
                    entregaUf: res.uf,
                });
                this.atualizarEstadoDoMunicipio(true);
                this.form.patchValue({ entregaCidade: res.localidade }, { emitEvent: false });

                if (!res.localidade || !this.idUnidadeFederativa) return;

                this.selectPesquisavelService.getSelectValues<MunicipioItem>(this.urlMunicipio, res.localidade).subscribe({
                    next: (municipios) => {
                        const municipio = municipios.find((m) => m.codIbge === res.ibge) ?? municipios[0] ?? null;

                        if (!municipio) return;

                        this.municipiosPreCarregados = [municipio];
                        this.municipioControl?.setValue(municipio.idMunicipio, { emitEvent: false });
                        this.form.patchValue({ entregaCidade: municipio.nome }, { emitEvent: false });
                    },
                });
            },
            error: () => {
                this.isBuscandoCep = false;
            },
        });
    }
}
